import React, { Suspense, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Navbar from '../Navbar';
import Sidebar from '../Sidebar';
import MobileShell from './MobileShell';
import { MobileFiltersSheet } from '../filters/MobileFiltersSheet';
import { FilterState } from '../../types';
import { MOBILE, DESKTOP } from '../../lib/mobileUI';

import { UserProfile } from '../../types';

interface AppShellProps {
    children: React.ReactNode;
    isGuest: boolean;
    shortlistCount: number;
    filters: FilterState; // Ideally passed via Context, but props for now
    onFilterChange: (newFilters: Partial<FilterState>) => void;
    onSearch: (query: string) => void;
    onLogout: () => void;
    isMobileFiltersOpen: boolean;
    setIsMobileFiltersOpen: (isOpen: boolean) => void;
    triggerGate: () => void;
    userProfile?: UserProfile | null;
    onPropertyClick?: (id: string | number, property?: any) => void;
}

const AppShell: React.FC<AppShellProps> = ({
    children,
    isGuest,
    shortlistCount,
    filters,
    onFilterChange,
    onSearch,
    onLogout,
    isMobileFiltersOpen,
    setIsMobileFiltersOpen,
    triggerGate,
    userProfile,
    onPropertyClick
}) => {
    const location = useLocation();
    const navigate = useNavigate();

    const handleProtectedNavigation = (path: string) => {
        if (isGuest) { triggerGate(); return; }
        navigate(path);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const showSidebar = location.pathname === '/marketplace';
    const hideLayout = location.pathname === '/login' || location.pathname === '/onboarding' || location.pathname === '/';

    // Show back button on all pages except main entry points
    const showBackButton = !hideLayout && location.pathname !== '/marketplace';
    // Add logic for /finder or others if they need hiding too, matching App.tsx logic

    const isFinder = location.pathname.startsWith('/finder');
    const isSecureUnit = location.pathname.startsWith('/_secure-unit');
    const isDetail = location.pathname.startsWith('/property/') || location.pathname.startsWith('/projects/');

    const layoutPaddingClass = (() => {
        if (hideLayout) return '';
        // Mobile: Use MOBILE tokens (px-4 sm:px-5 pt-16), Desktop: Use DESKTOP tokens (px-6 lg:px-12 pt-20 md:pt-28)
        // Special routes get adjusted bottom padding
        if (isFinder || isSecureUnit) {
            return 'px-4 sm:px-5 pt-16 pb-8 md:px-6 md:pt-20 lg:px-12 md:pb-8';
        }
        if (isDetail) {
            return 'px-4 sm:px-5 pt-16 pb-12 md:px-6 md:pt-20 lg:px-12 md:pb-12';
        }
        // Dashboard: Use full mobile/desktop tokens
        return 'px-4 sm:px-5 pt-16 pb-12 md:px-6 md:pt-20 lg:px-12 md:pb-12';
    })();

    // Mobile layout: Use MobileShell
    // Desktop layout: Use existing Navbar + Sidebar
    const [isMobile, setIsMobile] = useState(false);
    
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // For mobile, use MobileShell (which handles its own layout)
    if (isMobile && !hideLayout) {
        return (
            <MobileShell
                isGuest={isGuest}
                shortlistCount={shortlistCount}
                filters={filters}
                onFilterChange={onFilterChange}
                onSearch={onSearch || (() => {})}
                onLogout={onLogout}
                triggerGate={triggerGate}
                isMobileFiltersOpen={isMobileFiltersOpen}
                setIsMobileFiltersOpen={setIsMobileFiltersOpen}
                userProfile={userProfile}
                onPropertyClick={onPropertyClick}
            >
                {children}
            </MobileShell>
        );
    }

    // Desktop layout (unchanged)
    return (
        <div className="min-h-screen flex flex-col">
            {!hideLayout && (
                <Navbar
                    shortlistCount={shortlistCount}
                    onNavigateToShortlist={() => handleProtectedNavigation('/shortlist')}
                    onNavigateHome={() => navigate(isGuest ? '/' : '/marketplace')}
                    onNavigateToFinder={() => {}} // Property Finder disabled until later
                    onNavigateToProfile={() => handleProtectedNavigation('/account')}
                    onNavigateToSettings={() => handleProtectedNavigation('/settings')}
                    onNavigateToCalculator={() => handleProtectedNavigation('/tools/calculator')}
                    onNavigateToRentalYield={() => handleProtectedNavigation('/tools/rental-yield')}
                    onNavigateToMortgage={() => handleProtectedNavigation('/tools/mortgage')}
                    onNavigateToAnalytics={() => handleProtectedNavigation('/analytics')}
                    onNavigateToPortfolio={() => handleProtectedNavigation('/portfolio')}
                    onNavigateToDeveloperHub={() => handleProtectedNavigation('/developer-hub')}
                    onLogout={onLogout}
                    onSearch={onSearch} // Needs to handle routing to dashboard
                    isGuest={isGuest}
                    userProfile={userProfile}
                    onPropertyClick={onPropertyClick}
                />
            )}


            <div className={`${layoutPaddingClass} max-w-[1600px] mx-auto w-full min-h-screen flex`}>
                {/* Sidebar (Desktop) */}
                {showSidebar && (
                    <div className="hidden lg:block flex-shrink-0">
                        <Sidebar
                            filters={filters}
                            onFilterChange={onFilterChange}
                        />
                    </div>
                )}

                {/* Mobile Filters Sheet */}
                {showSidebar && (
                    <MobileFiltersSheet
                        open={isMobileFiltersOpen}
                        onClose={() => setIsMobileFiltersOpen(false)}
                        filters={filters}
                        onFilterChange={onFilterChange}
                        onClear={() =>
                            onFilterChange({
                                searchQuery: "",
                                locations: [],
                                priceMin: "",
                                priceMax: "",
                                beds: null,
                                baths: null,
                                type: [],
                                developer: "All Developers",
                                completion: [],
                                sqftMin: "",
                                sqftMax: "",
                                ppsfMin: "",
                                ppsfMax: "",
                                priceRange: [0, 50000000],
                                sqftRange: [0, 10000],
                            })
                        }
                        onApply={() => {
                            // no-op: the list already reacts to filters, but keep hook for future analytics
                        }}
                    />
                )}

                <main className={`flex-1 overflow-x-hidden overflow-y-auto ${showSidebar ? 'lg:ml-10' : ''}`}>
                    {/* Global Back Button */}
                    {showBackButton && (
                        <button
                            onClick={() => navigate(-1)}
                            className="mb-4 flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors group"
                        >
                            <div className="size-8 rounded-lg bg-slate-100 group-hover:bg-slate-200 flex items-center justify-center transition-colors">
                                <ArrowLeft size={16} />
                            </div>
                            Back
                        </button>
                    )}
                    <div className="w-full max-w-full overflow-x-hidden pr-1 md:pr-0">
                        {children}
                    </div>
                </main>
            </div>
        </div>
    );
};

export default AppShell;
